import { useQueryClient } from '@tanstack/react-query';
import type { MutableRefObject } from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { selectMedicationQueryDefinition } from '@/entities/rx';

import { applySelectedMedicationToForm } from './applySelectedMedicationToForm';
import type { RxFormApi } from './types';

export interface UseAlternativeSwitcherInput {
  form: RxFormApi;
  /**
   * When true, the returned ``selectAlternative`` is ``undefined`` so
   * callers (and the alternatives UI) can treat the panel as read-only
   * — matches the ``readOnly`` flag on Review for execute-only flows.
   */
  readOnly?: boolean;
  /**
   * Optional shared ref tracking the last auto-filled dispense-unit ID.
   * Pass the same ref used by :func:`MedicationSelector` so that
   * switching to an alternative correctly detects whether the current
   * unit was auto-filled or manually edited.
   */
  lastAutoDispenseUnitIdRef?: MutableRefObject<string | null>;
}

export interface UseAlternativeSwitcherResult {
  /**
   * Async swap entry point. Undefined when ``readOnly`` is set so
   * downstream alternative buttons render as plain text rows.
   */
  selectAlternative: ((dispensableDrugId: number) => Promise<void>) | undefined;
  /** True while a swap is in flight (block buttons / show spinners). */
  isSwitching: boolean;
}

/**
 * Shared "swap to a payer / therapeutic alternative" flow.
 *
 * Used by both the Details step (alternatives modal) and the Review
 * step (BenefitsCoordinationPanel). Re-uses the medication picker's
 * ``select_medication`` → :func:`applySelectedMedicationToForm`
 * pipeline so the dispense-unit auto-fill stays consistent regardless
 * of where the swap was initiated from.
 *
 * On success, surfaces a toast with the new medication's display name.
 * On failure (DoseSpot fetch error), surfaces a destructive toast and
 * leaves the form untouched.
 */
export function useAlternativeSwitcher({
  form,
  readOnly = false,
  lastAutoDispenseUnitIdRef: externalRef,
}: UseAlternativeSwitcherInput): UseAlternativeSwitcherResult {
  const queryClient = useQueryClient();
  const [isSwitching, setIsSwitching] = useState(false);
  const internalRef = useRef<string | null>(null);
  const lastAutoDispenseUnitIdRef = externalRef ?? internalRef;

  const selectAlternative = readOnly
    ? undefined
    : async (dispensableDrugId: number) => {
        setIsSwitching(true);
        try {
          const selected = await queryClient.fetchQuery(
            selectMedicationQueryDefinition({ dispensable_drug_id: dispensableDrugId })
          );
          applySelectedMedicationToForm({ form, selected, lastAutoDispenseUnitIdRef });
          toast.success('Switched to alternative.', {
            description: selected.DisplayName ?? selected.DrugName ?? undefined,
          });
        } catch (err: unknown) {
          toast.error('Could not switch to alternative.', {
            description:
              err instanceof Error
                ? err.message
                : 'Medication details could not be fetched from DoseSpot.',
          });
        } finally {
          setIsSwitching(false);
        }
      };

  return { selectAlternative, isSwitching };
}
